import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star } from "./Star";
import { TopBand, MidBand, BotBand } from "./Bands";
import "./intro.css";

export const shouldPlayIntro = () => true;

const FONT = "'Cormorant Garamond', 'Times New Roman', serif";
const textProps = {
  x: 600,
  y: 258,
  textAnchor: "middle",
  fontFamily: FONT,
  fontWeight: 700,
  fontSize: 300,
  letterSpacing: 14,
};

const Wordmark = () => (
  <svg viewBox="0 0 1200 300" className="w-full" data-testid="intro-wordmark" aria-label="Vincen">
    <defs>
      <clipPath id="vincen-clip">
        <text {...textProps}>VINCEN</text>
      </clipPath>
      <clipPath id="band-clip">
        <rect x="0" y="0" width="1200" height="64" />
      </clipPath>
      <linearGradient id="intro-sheen-grad" x1="0" x2="1">
        <stop offset="0" stopColor="#F5F5F5" stopOpacity="0" />
        <stop offset="0.5" stopColor="#F5F5F5" stopOpacity="0.55" />
        <stop offset="1" stopColor="#F5F5F5" stopOpacity="0" />
      </linearGradient>
      <filter id="intro-glow" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="2" />
      </filter>
    </defs>
    <g clipPath="url(#vincen-clip)">
      <rect width="1200" height="300" fill="#0A0A0A" />
      <g transform="translate(0 67)" clipPath="url(#band-clip)" style={{ filter: "grayscale(1)" }}>
        <TopBand />
      </g>
      <g transform="translate(0 131)" clipPath="url(#band-clip)" style={{ filter: "grayscale(1)" }}>
        <MidBand />
      </g>
      <g transform="translate(0 195)" clipPath="url(#band-clip)" style={{ filter: "grayscale(1)" }}>
        <BotBand />
      </g>
      <rect y="130" width="1200" height="1.6" fill="#D4AF37" opacity="0.85" />
      <rect y="194" width="1200" height="1.6" fill="#D4AF37" opacity="0.85" />
      <rect width="320" height="300" fill="url(#intro-sheen-grad)" className="intro-sheen" />
    </g>
    <text {...textProps} fill="none" stroke="#D4AF37" strokeOpacity="0.6" strokeWidth="1">
      VINCEN
    </text>
  </svg>
);

const IntroScene = ({ onDone }) => {
  const [phase, setPhase] = useState("star");

  useEffect(() => {
    document.fonts?.load(`600 250px "Cormorant Garamond"`).catch(() => {});
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    const timers = [
      setTimeout(() => setPhase("shine"), 1300),
      setTimeout(() => setPhase("word"), 2100),
      setTimeout(() => setPhase("out"), 7200),
      setTimeout(() => onDone(), 8000),
    ];
    return () => {
      timers.forEach(clearTimeout);
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
  }, [onDone]);

  const wordVisible = phase === "word" || phase === "out";

  const skip = () => {
    if (phase === "out") return;
    setPhase("out");
    setTimeout(() => onDone(), 500);
  };

  return (
    <motion.div
      data-testid="intro-scene"
      data-phase={phase}
      onPointerDown={skip}
      className="fixed inset-0 z-[300] bg-black flex flex-col items-center justify-center overflow-hidden cursor-pointer"
      animate={{ opacity: phase === "out" ? 0 : 1, scale: phase === "out" ? 1.04 : 1 }}
      transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(107,31,42,0.18),transparent_60%)]" />

      <motion.div
        className="relative"
        initial={{ y: 90, opacity: 0, scale: 0.8 }}
        animate={{ y: wordVisible ? -10 : 0, opacity: 1, scale: 1 }}
        transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
      >
        <Star className={`w-32 h-32 md:w-44 md:h-44 relative ${phase !== "star" ? "intro-star-shine" : ""}`} />
      </motion.div>

      {wordVisible && (
        <motion.div
          className="w-[94vw] max-w-[1500px] mt-2 md:mt-4"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <Wordmark />
          <motion.p
            className="intro-tagline -mt-1 md:mt-1 text-center text-[11px] md:text-sm tracking-[0.55em] uppercase font-medium"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.8, duration: 1 }}
          >
            The One Who Conquers
          </motion.p>
        </motion.div>
      )}
    </motion.div>
  );
};

export default IntroScene;
